import { HttpClient } from '@angular/common/http';

export interface SourceItem {
  source: string;
  name: string;
  description: string;
  link?: string;
  country_name?: string;
  source_name?: string;
  [key: string]: any; // Allow for additional fields
}

let sourcesCache: SourceItem[] | null = null;

/**
 * Load the sources JSON file
 * @param http The HttpClient instance
 * @returns Promise with the sources data
 */
export async function loadSources(http: HttpClient): Promise<SourceItem[]> {
  if (sourcesCache) {
    return sourcesCache;
  }

  try {
    const jsonPath = 'assets/data/list_sources.json';
    // Use toPromise() which is available in older RxJS versions
    const response = await http.get<any>(jsonPath).toPromise();
    // The actual data might be nested in the JSON structure
    const sources = response.sources || response;
    sourcesCache = sources;
    return sources;
  } catch (error) {
    console.error('Error loading sources:', error);
    throw new Error('Failed to load sources data');
  }
}

/**
 * Get a source by its field name
 * @param sourceField The field name to search for
 * @param http The HttpClient instance
 * @returns Promise with the found source or null if not found
 */
export async function getSourceByField(
  sourceField: string,
  http: HttpClient
): Promise<SourceItem | null> {
  if (!sourceField) {
    return null;
  }

  const sources = await loadSources(http);
  return sources.find(s => s.source === sourceField) || null;
}

/**
 * Search sources by a query string
 * @param query The query string to search for
 * @param http The HttpClient instance
 * @returns Promise with matching sources
 */
export async function searchSources(
  query: string,
  http: HttpClient
): Promise<SourceItem[]> {
  const sources = await loadSources(http);
  
  if (!query || query.trim() === '') {
    return sources;
  }

  const lowerQuery = query.toLowerCase();
  return sources.filter(source => 
    source.source.toLowerCase().includes(lowerQuery) || 
    source.name.toLowerCase().includes(lowerQuery) ||
    (source.description && source.description.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Clears the sources cache
 */
export function clearSourcesCache(): void {
  sourcesCache = null;
} 